import { Edit } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import moment from "moment/moment";
import * as XLSX from "xlsx";
import { companiesColumnData } from "../../common/constants/gridRowColumn";
import { useAuth, useCompany, useDealerCompany } from "../../common/hooks";
import { DeleteDialog } from "../../components/DeleteDialog";
import { SearchBar } from "../../components/SearchBar";
import { useDebounce } from "../../components/useDebounce";
import { layoutStyle } from "../layout-ui/style";
import AddCompany from "./AddCompany";
import { TruckLoader } from "./truckLoader";
import {
  actionColumnFunction,
  checkbox,
  getDefaultColDef,
  isAdmin,
} from "../../common/constants/commonFunction";

export const ManageCompanies = () => {
  const classes = layoutStyle();
  const [selectedRows, setSelectedRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("Add Company");
  const [searchValue, setSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [columnFinal, setColumnFinal] = useState([]);
  const [anchorElPrint, setAnchorElPrint] = useState(null);
  const [anchorElSettings, setAnchorElSettings] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElGroupBy, setAnchorElGroupBy] = useState(null);
  const [anchorElFavorites, setAnchorElFavorites] = useState(null);
  const [anchorElExport, setAnchorElExport] = useState(null);
  const [anchorElSearch, setAnchorElSearch] = useState(null);
  const [rowDataValue, setRowDataValue] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [page, setPage] = useState({ pageSize: 10000, page: 0 });
  const [editValue, setEditValue] = useState();
  const [mainData, setMainData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [clickedImageUrl, setClickedImageUrl] = useState("");
  const {
    company,
    getCompanyApi,
    loading,
    companyName,
    addCompanyApi,
    deleteCompanyApi,
  } = useCompany();
  const { getDealerCompanyApi } = useDealerCompany();
  const userType = localStorage.getItem("type");
  const { userId } = useAuth();
  const [deleteValue, setDeleteValue] = useState();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const defaultColDef = useMemo(() => getDefaultColDef(), []);

  const handleClickSearch = (event) => {
    setAnchorElSearch(event.currentTarget);
  };

  const handleClickPrint = (event) => {
    setAnchorElPrint(event.currentTarget);
  };

  const handleClickSettings = (event) => {
    setAnchorElSettings(event.currentTarget);
  };

  const handleClickExport = (event) => {
    setAnchorElExport(event.currentTarget);
  };

  const handleCloseExport = () => {
    setAnchorElExport(null);
  };

  const getCompanyExportRows = (format) => {
    const preserveCsvFormatting = (value) =>
      format === "csv" && value ? `="${value}"` : value;

    return mainData?.map((row) => {
      const hasAnalysisGenerated = [
        row?.analysisGenerated,
        row?.data?.analysisGenerated,
        row?.data?.data?.analysisGenerated,
      ].some((value) => {
        return value === true;
      });

      const users = Array.isArray(row?.users)
        ? row.users
        : Array.isArray(row?.data?.users)
          ? row.data.users
          : Array.isArray(row?.data?.data?.users)
            ? row.data.data.users
            : [];

      const userName = users
        .map((user) => user?.userName || user?.UserName || "")
        .filter(Boolean)
        .join(", ");

      const userCode = users
        .map((user) => {
          const code = user?.code?.toString() || "";
          return /^\d+$/.test(code) ? code.padStart(4, "0") : code;
        })
        .filter(Boolean)
        .join(", ");

      const createdAt =
        row?.createdAt ||
        row?.data?.data?.createdAt ||
        row?.data?.createdAt ||
        "";

      return {
        "Company Name": row?.companyName || row?.data?.data?.companyName || "",
        "Dealer Name": row?.dealerName || row?.data?.data?.dealerName || "",
        "Date (Month/Day/Year)": preserveCsvFormatting(
          createdAt ? moment(createdAt).format("MM/DD/YYYY") : "",
        ),
        User: hasAnalysisGenerated ? userName : "",
        "User Code": hasAnalysisGenerated
          ? preserveCsvFormatting(userCode.toString())
          : "",
        "Analysis Generated": hasAnalysisGenerated ? "Yes" : "No",
      };
    });
  };

  const handleExportFile = (format) => {
    const rows = getCompanyExportRows(format);
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Manage Companies");
    const dateTag =
      startDate && endDate
        ? `_${startDate}_to_${endDate}`
        : startDate
          ? `_from_${startDate}`
          : endDate
            ? `_until_${endDate}`
            : "";
    const fileName =
      format === "csv"
        ? `ManageCompanies${dateTag}.csv`
        : `ManageCompanies${dateTag}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    handleCloseExport();
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  const onSearch = (e) => {
    debouncedSearch(e?.target?.value);
    handleClickSearch(e);
  };

  const handleClose = () => {
    setAnchorElPrint(null);
    setAnchorElSettings(null);
    setAnchorElFilter(null);
    setAnchorElGroupBy(null);
    setAnchorElFavorites(null);
  };

  const onAddBtnClick = () => {
    setOpen(true);
    setTitle("Add Company");
    setEditValue(null);
  };
  const handleEditButton = (value) => {
    setOpen(true);
    setTitle("Edit Company");
    setEditValue(value);
  };

  const handleImageClick = (imageUrl) => {
    setClickedImageUrl(imageUrl);
    setOpenModal(true);
  };
  const handleCloseImageModal = () => {
    setOpenModal(false);
  };

  const handleDeleteButton = async (values) => {
    setDeleteOpen(true);
    setDeleteValue(values?.id);
    // deleteCompanyApi(values);
  };

  const handleDelete = async () => {
    await deleteCompanyApi(deleteValue);
    setDeleteOpen(false);
  };

  useEffect(() => {
    if (companiesColumnData) {
      const actionColumn = actionColumnFunction({
        selectedRows: selectedRows,
        handleEditButton: handleEditButton,
        handleDeleteButton: handleDeleteButton,
        deleteButton: isAdmin(),
      });
      // let imageColumn = {
      //   field: "companyImage",
      //   headerName: "Company Logo",
      //   sortable: false,
      //   resizable: false,
      //   width: 140,
      //   headerClassName: "super-app-theme--header",
      //   disableClickEventBubbling: true,
      //   cellRenderer: (params) => (
      //     <img
      //       src={`data:image/png;base64,${params?.data?.companyImage}`}
      //       style={{ margin: 4 }}
      //       height={30}
      //       width={30}
      //       onClick={() => handleImageClick(params?.data?.companyImage)}
      //     />
      //   ),
      // };
      setColumnFinal([checkbox, ...companiesColumnData, actionColumn]);
    }
  }, [companiesColumnData, selectedRows]);

  const onSearchFunc = (e) => {
    setSearchValue(e[0]);
  };
  const debouncedSearch = useDebounce(onSearchFunc, 500);

  // Populate rowDataValue whenever raw API data arrives
  useEffect(() => {
    if (companyName?.length > 0 && userType === "dealer") {
      setRowDataValue([...companyName]);
    } else if (company?.length > 0 && userType === "admin") {
      setRowDataValue([...company]);
    }
  }, [companyName, company, userType]);

  // Apply search + timeframe filter and set mainData
  useEffect(() => {
    const source =
      userType === "dealer" ? (companyName ?? []) : (company ?? []);

    // Unwrap dealer rows to the inner data object
    const normalised = source.map((row) =>
      userType === "dealer" ? (row?.data?.data ?? row) : row,
    );

    const filtered = normalised.filter((row) => {
      // Search by company name
      if (searchValue) {
        const name = row?.companyName?.toLowerCase() ?? "";
        if (!name.includes(searchValue.toLowerCase())) return false;
      }

      // Filter by date range
      if (startDate || endDate) {
        const createdAt = row?.createdAt ? new Date(row.createdAt) : null;
        if (!createdAt) return false;
        if (startDate && createdAt < new Date(startDate)) return false;
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (createdAt > end) return false;
        }
      }

      return true;
    });

    setMainData(filtered);
  }, [searchValue, startDate, endDate, companyName, company, userType]);

  useEffect(() => {
    if (userType?.toLowerCase() === "admin") {
      getCompanyApi({ limit: page.pageSize, offset: page.page });
    } else if (userType?.toLowerCase() === "dealer") {
      getDealerCompanyApi({
        userId: userId,
        limit: page.pageSize,
        offset: page.page,
      });
    }
  }, [refreshData]);
  const ImageModal = () => {
    return (
      <Modal open={openModal} onClose={handleCloseImageModal}>
        <Box
          sx={{
            position: "absolute",
            outline: "none",
            overflow: "hidden",
            margin: "20px auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              bgcolor: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: 24,
              borderRadius: 4,
              maxHeight: "400px",
              maxWidth: "400px",
              padding: 10,
            }}
          >
            <IconButton
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                color: "rgba(0, 0, 0, 0.54)",
              }}
              onClick={handleCloseImageModal}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={`data:image/png;base64,${clickedImageUrl}`}
              alt="Company Logo"
              width={400}
              height={400}
            />
          </Box>
        </Box>
      </Modal>
    );
  };

  return (
    <Box className={classes.mainContentCompany}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Box className={classes.headerNameText}>Companies</Box>
        <Box className={classes.addEditGrid}>
          <Button
            variant="outlined"
            className={[classes.fleetListNewButton, classes.fontSize12]}
            onClick={onAddBtnClick}
          >
            Add
          </Button>
          <Button
            variant="outlined"
            className={[classes.fleetListNewButton, classes.fontSize12]}
            onClick={handleClickExport}
          >
            Export
          </Button>
          <Menu
            anchorEl={anchorElExport}
            open={Boolean(anchorElExport)}
            onClose={handleCloseExport}
          >
            <MenuItem onClick={() => handleExportFile("xlsx")}>
              Export XLSX
            </MenuItem>
            <MenuItem onClick={() => handleExportFile("csv")}>
              Export CSV
            </MenuItem>
          </Menu>
          <SearchBar onSearch={onSearch} />
        </Box>
      </Stack>

      {/* ── Row 2: Timeframe filter ── */}
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
          mt: "6px",
          mb: "4px",
          padding: "6px 10px",
          backgroundColor: "#f5f7f5",
          borderRadius: "6px",
          border: "1px solid #dde8da",
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: "#18333A",
            minWidth: "max-content",
          }}
        >
          Timeframe:
        </Typography>
        <TextField
          type="date"
          size="small"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        <Typography sx={{ fontSize: "12px", color: "#555" }}>to</Typography>
        <TextField
          type="date"
          size="small"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          sx={{
            "& .MuiInputBase-input": { fontSize: "12px", padding: "6px 10px" },
            "& .MuiInputLabel-root": { fontSize: "12px" },
            "& .MuiOutlinedInput-root": { borderRadius: "4px" },
            minWidth: 150,
          }}
        />
        {(startDate || endDate) && (
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearDates}
            sx={{
              fontSize: "11px",
              textTransform: "none",
              color: "error.main",
              borderColor: "error.main",
              padding: "3px 10px",
              "&:hover": {
                backgroundColor: "error.light",
                color: "#fff",
                borderColor: "error.main",
              },
            }}
          >
            Clear
          </Button>
        )}
        {(startDate || endDate) && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#18333A",
              fontStyle: "italic",
              ml: 1,
            }}
          >
            {`Showing ${mainData?.length ?? 0} record(s)`}
          </Typography>
        )}
      </Stack>

      {loading ? (
        <TruckLoader />
      ) : (
        <Box className={classes.gridContantDealer}>
          <AgGridReact
            className="ag-theme-quartz"
            onRowSelectionModelChange={(ids) => {
              setSelectedRows(ids);
            }}
            rowSelection="multiple"
            suppressRowClickSelection
            pagination={true}
            rowData={mainData}
            defaultColDef={defaultColDef}
            columnDefs={columnFinal}
            checkboxSelection
          />
        </Box>
      )}
      {open && (
        <AddCompany
          setOpen={setOpen}
          open={open}
          title={title}
          setRefreshData={setRefreshData}
          editValue={editValue}
        />
      )}
      <ImageModal />

      {deleteOpen && (
        <DeleteDialog
          open={deleteOpen}
          setOpen={() => setDeleteOpen(false)}
          deleteApi={() => handleDelete()}
        />
      )}
    </Box>
  );
};
